import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import _debounce from "lodash/debounce";
import { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { usePlanFeature } from "~/hooks/usePlanFeature";
import Modal from "~/components/ui/Modal";
import { SVG } from "~/components/ui/SVG";
import {
  SHOW_LIMITS_MODAL,
  triggerShowPlansModal,
  triggerShowResumeModal,
} from "~/helpers/customEvents";
import { Limits } from "~/types/billing";
import * as S from "./styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFeaturesTranslations } from "~/constants/features";
import FeaturesTestimonialImg from "~/assets/png/features-testimonial.png";
import { useMinimumPlanForLimit } from "~/hooks/useMinimumPlanForLimit";
import { usePlans } from "~/api/billing/queries";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { useNextPlanForLimit } from "~/hooks/useNextPlanForLimit";
import { useHandlePlanPayment } from "~/pages/checkout/useHandlePlanPayment";
import { usePaymentMethod } from "~/hooks/usePaymentMethod";
import { useStripeConfirmation } from "~/pages/checkout/useStripeConfirmation";
import handleErrors from "~/helpers/handleErrors";
import { useShopifyPayment } from "~/pages/checkout/useShopifyPayment";
import { useSetupIntent } from "~/pages/checkout/useSetupIntent";
import { useAccount } from "~/hooks/useAccount";
import { toast } from "../toast";
// @ts-ignore
import LoaderSVG from "~/assets/svg/loader.svg?react";

interface Props {
  limitType: Limits;
  hide: () => void;
}

const stripePromise: Promise<Stripe | null> = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
);

const LimitsModalInternal = ({ limitType, hide }: Props) => {
  const { t } = useTranslation();
  const { minimumPlan, hasAbovePlans } = useMinimumPlanForLimit(limitType);
  const navigate = useNavigate();
  const { account } = useAccount();
  if (!minimumPlan) return null;
  const { isUserShopify } = usePaymentMethod();
  const { nextPlan } = useNextPlanForLimit(limitType, account?.active_subscription?.plan);
  const [isLoading, setIsLoading] = useState(false);
  const payShopify = useShopifyPayment(nextPlan?.id || "");

  const translations = getFeaturesTranslations(
    t,
    limitType,
    minimumPlan.name,
    hasAbovePlans
  );

  const handleShopifyPayment = async () => {
    const messages = {
      success: null,
      error: t("checkout.error"),
      loading: null,
    };
    const { response, errors } = await handleErrors(payShopify, messages);
    if (!errors) {
      localStorage.removeItem("onboarding_with_create_store");
    }

    if (response?.confirmation_url)
      window.location.replace(response.confirmation_url);
  };


  const hideModal = () => {
    hide();
  }

  const getSetupIntent = useSetupIntent();
  const [clientSecret, setClientSecret] = useState();

  const getClientSecretDebounced = useCallback(
    _debounce(async (account) => {
      if (!clientSecret) {
        const response = await getSetupIntent(account);
        setClientSecret(response?.client_secret);
      }
    }, 200),
    [clientSecret]
  );

  useEffect(() => {
    if (!account) {
      return;
    }
    getClientSecretDebounced(account);
  }, [account]);

  const CheckoutStripe = () => {
    const upgradeWithStore =
      localStorage.getItem("onboarding_with_create_store") === "true";

    const { isConfirming, handlePayment: handleStripePayment, } = useStripeConfirmation(
      nextPlan,
      upgradeWithStore,
      (value) => { setIsLoading(value) },
      () => {
        hide();
        toast.success("Congratulations! Your plan was successfully upgraded");
      },
      true,
    );

    if (isLoading) {
      return <LoaderSVG />

    }
    return (
      <S.SwitchButton
        onClick={() => {
          if (!minimumPlan.id) {
            triggerShowPlansModal();
          } else {
            handleStripePayment();
          }
        }}>
        {t("features.switch_to_plan", { planName: nextPlan?.name })}
      </S.SwitchButton>
    );
  };


  const CheckoutStripeWrapper = () => {
    const appearance = {
      theme: "stripe",
    };
    const options = {
      clientSecret: clientSecret,
      appearance,
    };

    return (
      //@ts-ignore
      <Elements options={options} stripe={stripePromise}>
        <CheckoutStripe />
      </Elements>
    );
  };

  if (limitType === Limits.PREMIUM_PRODUCTS || limitType === Limits.LIVE_PRODUCTS) {
    const texts = getFeaturesTranslations(
      t,
      limitType,
      nextPlan?.name ?? '',
      false
    );

    return (
      <Modal
        id="plan-limits"
        className="plan-limits"
        isShowing={true}
        hide={hideModal}
        padding="24px"
        minWidth="min(90%, 584px)">
        <S.ModalContent>
          <S.TopSection>
            <S.IconContainer>
              <SVG icon={faLock} color="primary" size="xl" />
            </S.IconContainer>
            <S.Title>{texts.title}</S.Title>
            <S.Description>{texts.description}</S.Description>
            <S.TestimonialContainer>
              <S.TestimonialImage src={FeaturesTestimonialImg} />
              <S.TestimonialText>{texts.testimonial}</S.TestimonialText>
            </S.TestimonialContainer>
          </S.TopSection>
          <S.BottomSection>
            {isUserShopify ?
              <S.SwitchButton
                onClick={() => {
                  hide();
                  if (!minimumPlan.id) {
                    triggerShowPlansModal();
                  } else {
                    handleShopifyPayment();
                  }
                }}>
                {t("features.switch_to_plan", { planName: nextPlan?.name })}
              </S.SwitchButton> :
              <CheckoutStripeWrapper />
            }
            <S.DisclaimerText>
              {t("features.plan_disclaimer", { planName: nextPlan?.name })}
            </S.DisclaimerText>
          </S.BottomSection>
        </S.ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      id="plan-limits"
      className="plan-limits"
      isShowing={true}
      hide={hideModal}
      padding="24px"
      minWidth="min(90%, 584px)">
      <S.ModalContent>
        <S.TopSection>
          <S.IconContainer>
            <SVG icon={faLock} color="primary" size="xl" />
          </S.IconContainer>
          <S.Title>{translations.title}</S.Title>
          <S.Description>{translations.description}</S.Description>
          <S.TestimonialContainer>
            <S.TestimonialImage src={FeaturesTestimonialImg} />
            <S.TestimonialText>{translations.testimonial}</S.TestimonialText>
          </S.TestimonialContainer>
        </S.TopSection>
        <S.BottomSection>
          {isUserShopify ?
            <S.SwitchButton
              onClick={() => {
                hide();
                if (!minimumPlan.id) {
                  triggerShowPlansModal();
                } else {
                  handleShopifyPayment();
                }
              }}>
              {t("features.switch_to_plan", { planName: minimumPlan.name })}
            </S.SwitchButton> :
            <CheckoutStripeWrapper />
          }
          <S.DisclaimerText>
            {t("features.plan_disclaimer", { planName: minimumPlan.name })}
          </S.DisclaimerText>
        </S.BottomSection>
      </S.ModalContent>
    </Modal>
  );
};

export const LimitsModal = () => {
  const [shownLimit, setShownLimit] = useState<Limits | undefined>(undefined);
  const { isFeaturePaused } = usePlanFeature();
  const { data: plans } = usePlans();

  useEffect(() => {
    const handleEvent = ((e: CustomEvent<{ limitType: Limits }>) => {
      if (isFeaturePaused()) {
        triggerShowResumeModal();
      } else {
        setShownLimit(e.detail.limitType);
      }
    }) as EventListener;

    window.addEventListener(SHOW_LIMITS_MODAL, handleEvent);

    return () => {
      window.removeEventListener(SHOW_LIMITS_MODAL, handleEvent);
    };
  }, [isFeaturePaused]);

  if (!shownLimit || !plans || DISABLE_PAYMENTS) return null;

  return ReactDOM.createPortal(
    <>
      <S.LimitsModalCloseButtonStyles />
      <LimitsModalInternal
        limitType={shownLimit}
        hide={() => setShownLimit(undefined)}
      />
    </>,
    document.body
  );
};
